// src/pages/LogHistoryCalendarView.tsx
import { useState, useMemo } from "react";
import {
  getMonth,
  getYear,
  eachDayOfInterval,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  isSameMonth,
  isSameDay,
} from "date-fns";
import { useLang } from "../contexts/LangContext";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { useParams } from "react-router-dom";
import { useUserData } from "../contexts/UserContext";
import { convertNumberInCurrentLang, getLocalDateString, convertWeight } from "../utils/misc";
import { useSwipe } from "../hooks/useSwipe";
import { AnimatePresence, motion } from "framer-motion";
import RespondentDetailCard from "../cards/RespondentDetails";
import { ModalWindow } from "../components/ModalWindow";

export default function LogHistoryCalendarView() {
  const { personId } = useParams();
  const { user, getPerson } = useUserData();

  const { lang, t } = useLang();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [modalChildren, setModalChildren] = useState<React.ReactNode>(null);

  const today = new Date();
  const todayMonth = getMonth(today);
  const todayYear = getYear(today);

  const [currentMonth, setCurrentMonth] = useState(getMonth(today));
  const [currentYear, setCurrentYear] = useState(getYear(today));
  const [direction, setDirection] = useState<1 | -1>(1);

  const swipeHandlers = useSwipe(
    () => handleNextMonth(),
    () => handlePrevMonth()
  );

  const person = getPerson(personId!);
  const isUser = !!user && !!person && user.id === person.id;

  // Determine where user came from and what the back button should do/say
  // Must be before early return to follow Rules of Hooks
  const backInfo = useMemo(() => {
    const from = searchParams.get('from');
    const fromPerson = searchParams.get('from_person');
    
    if (from === 'all_logs') {
      return { 
        text: 'logHistoryDateLog_BackToAllLogs',
        action: () => navigate('/survey/all-logs') 
      };
    }
    
    // Default behavior based on whether it's the user or a surveyed person
    if (isUser) {
      return { 
        text: 'back',
        action: () => navigate('/user') 
      };
    } else {
      // If we have from_person, go back to that person's card in the list
      const personListUrl = fromPerson 
        ? `/survey/person-list#person-${fromPerson}`
        : '/survey/person-list';
      return { 
        text: 'backToPersonList',
        action: () => navigate(personListUrl) 
      };
    }
  }, [searchParams, navigate, isUser]);

  if (!person)
    return <p className="p-8 text-center text-red-600">Person not found</p>;

  // Dates with fish consumption (green)
  const datesWithLogs = person.records
    .filter((record) => record.meals && record.meals.length > 0)
    .map((record) => record.date);
  
  // Dates explicitly marked as no fish consumed (red)
  const datesWithNoFish = person.records
    .filter((record) => record.no_fish_consumed === true)
    .map((record) => record.date);

  // Per day consumption of fish in grams for dates with logs
  // For UI limitation, use unit "gm" for all calculations, ignoring user.appData.weight_unit
  const perDayConsumption: Record<string, number> = {};
  person.records.forEach((record) => {
    if (record.meals && record.meals.length > 0) {
      const totalFish = record.meals.reduce((total, meal) => {
        return (
          total +
          (meal.fish_items?.reduce(
            (sum, item) => sum + convertWeight(item.estimated_weight || 0, item.weight_unit || "gm", "gm"),
            0
          ) || 0)
        );
      }, 0);
      perDayConsumption[record.date] = totalFish;
    }
  });

  const monthStart = new Date(currentYear, currentMonth, 1);
  const monthEnd = endOfMonth(monthStart);
  const calendarStart = startOfWeek(monthStart, { weekStartsOn: 0 });
  const calendarEnd = endOfWeek(monthEnd, { weekStartsOn: 0 });

  const days = eachDayOfInterval({ start: calendarStart, end: calendarEnd });

  const handlePrevMonth = () => {
    setDirection(-1);
    const newDate = new Date(currentYear, currentMonth - 1, 1);
    setCurrentMonth(newDate.getMonth());
    setCurrentYear(newDate.getFullYear());
  };

  const handleNextMonth = () => {
    setDirection(1);
    const newDate = new Date(currentYear, currentMonth + 1, 1);
    setCurrentMonth(newDate.getMonth());
    setCurrentYear(newDate.getFullYear());
  };

  const handleSelectDate = (dateString: string) => {
    const from = searchParams.get('from');
    const dateUrl = `/log-history/${person.id}/${dateString}`;
    // Pass calendar's origin as calendar_from so DateLog knows to return to calendar
    navigate(from ? `${dateUrl}?calendar_from=${from}` : dateUrl);
  };

  const calendarKey = `month-${currentMonth}`;

  const swipeVariants = {
    enter: (dir: number) => ({
      x: dir > 0 ? 300 : -300,
      opacity: 0,
    }),
    center: {
      x: 0,
      opacity: 1,
    },
    exit: (dir: number) => ({
      x: dir > 0 ? -300 : 300,
      opacity: 0,
    }),
  };

  const weekDay = (day: string) => {
    const weekdays = {
      Su: "রবি",
      Mo: "সোম",
      Tu: "মঙ্গল",
      We: "বুধ",
      Th: "বৃহঃ",
      Fr: "শুক্র",
      Sa: "শনি",
    };
    return lang === "bn" ? weekdays[day as keyof typeof weekdays] : day;
  };

  const handlePersonNameClick = () => {
    if (!person) return;
    const modalChildren = (
      <div className="bg-white bg-opacity-75 backdrop-blur-md rounded-3xl shadow-lg mx-scr p-4">
        <div className="flex flex-col gap-4 ">
          <RespondentDetailCard person={person} />        
        </div>
      </div>
    );

    setModalChildren(modalChildren);
    //window.alert(modalChildren)
  };

  const modalCloseHandler = () => {
    setModalChildren(null);
  };

  return (
    <motion.div className="flex flex-col grow"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}>
      <div className="flex flex-col items-center justify-start grow">
        <div className="flex flex-col w-full justify-start items-center grow mt-6 px-scr">
          <h1 className="text-2xl font-bold text-center text-rose-700">
            {/* {t("logHistory_Heading").replace("{name}", person.name || "")} */}
            {lang === "bn" ? (
              <span>
                <a
                  onClick={handlePersonNameClick}
                  className="font-bold cursor-pointer"
                >
                  {person.name || "---"}{" "}
                </a> 
                <span>এর মাছ খাওয়ার লগ</span>
              </span>
            ) : (
              <span>
                <span>Log History of </span>
                <a
                  onClick={handlePersonNameClick}
                  className="font-bold cursor-pointer"
                > {person.name || "---"}</a>
              </span>
            )}
          </h1>

          <div className="flex justify-between items-center w-full my-6">
            <button
              onClick={handlePrevMonth}
              className="button-base button-bg-grad-light-blue !px-6"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth="1.5"
                stroke="currentColor"
                className="size-6"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M10.5 19.5 3 12m0 0 7.5-7.5M3 12h18"
                />
              </svg>
            </button>
            <h2 className="text-xl font-bold text-blue-500">
              {new Date(currentYear, currentMonth, 1).toLocaleDateString(
                lang === "bn" ? "bn-BD" : "en-US",
                {
                  month: "long",
                  year: "numeric",
                }
              )}
            </h2>
            <button
              onClick={handleNextMonth}
              className="button-base button-bg-grad-light-blue !px-6"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth="1.5"
                stroke="currentColor"
                className="size-6"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M13.5 4.5 21 12m0 0-7.5 7.5M21 12H3"
                />
              </svg>
            </button>
          </div>

          <div className="relative w-full flex h-[320px] flex-col overflow-hidden">
            <AnimatePresence custom={direction} mode="sync" initial={false}>
              <motion.div
                key={calendarKey}
                custom={direction}
                variants={swipeVariants}
                initial="enter"
                animate="center"
                exit="exit"
                transition={{ duration: 0.3, ease: "easeInOut" }}
                className="absolute w-full h-full"
              >
                <div
                  className="w-full grid grid-cols-7 gap-y-1 text-sm text-center justify-items-center items-start"
                  {...swipeHandlers}
                >
                  {["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"].map((d) => (
                    <div key={d} className="font-medium text-gray-500 mb-1">
                      {weekDay(d)}
                    </div>
                  ))}
                  {days.map((day: Date) => {
                    const dateString = getLocalDateString(day);
                    const hasLog = datesWithLogs.includes(dateString);
                    const hasNoFish = datesWithNoFish.includes(dateString);
                    const inMonth = isSameMonth(day, monthStart);
                    const isToday = isSameDay(day, today);
                    
                    return (
                      <button
                        key={dateString}
                        onClick={() => handleSelectDate(dateString)}
                        className={`w-11 h-11 relative flex items-center justify-center text-xl !p-0 rounded-full transition-all 
                      ${
                        isToday && !hasLog && !hasNoFish
                          ? "border border-green-300"
                          : ""
                      }
                      ${
                        inMonth
                          ? `${day > today ? "text-rose-300" : "text-black"}`
                          : "text-gray-400"
                      } 
                      ${
                        hasLog
                          ? "button-bg-grad-green !text-2xl !text-white shadow-md"
                          : hasNoFish
                          ? "bg-gradient-to-br from-rose-500 to-rose-600 !text-2xl !text-white shadow-md shadow-rose-200"
                          : `${day > today ? "bg-rose-50" : "bg-gray-100"}`
                      }`}
                        disabled={day > today}
                      >
                        {convertNumberInCurrentLang(lang, day.getDate())}
                        {hasLog && (
                          <span className="absolute -bottom-2 left-1/2 translate-x-[-50%] text-[.5rem] text-white">
                            {convertNumberInCurrentLang(
                              lang,
                              perDayConsumption[dateString] || 0
                            )}
                            {lang === "bn" ? "গ্রা" : "g"}
                          </span>
                        )}
                        {hasNoFish && (
                          <span className="absolute -bottom-2 left-1/2 translate-x-[-50%] text-[.5rem] text-white opacity-90">
                            {lang === "bn" ? "০গ্রা" : "0g"}
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              </motion.div>
            </AnimatePresence>
          </div>

          {(currentMonth !== todayMonth || currentYear !== todayYear) && (
            <motion.div
              className="my-4 flex items-center justify-center w-full"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 20 }}
              transition={{
                opacity: { duration: 0.4 },
                y: { duration: 0.2, ease: "easeOut" },
              }}
            >
              <button
                onClick={() => {
                  setCurrentMonth(todayMonth);
                  setCurrentYear(todayYear);
                }}
                className="flex items-center justify-center button-small button-bg-grad-2 gap-3 !px-6"
              >
                {((currentYear > todayYear) || (currentYear === todayYear && currentMonth > todayMonth)) && (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth="1.5"
                    stroke="currentColor"
                    className="size-5"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M10.5 19.5 3 12m0 0 7.5-7.5M3 12h18"
                    />
                  </svg>
                )}
                <span>{t("logHistory_goToCurrentMonth")}</span>
                {((currentYear < todayYear) || (currentYear === todayYear && currentMonth < todayMonth)) && (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth="1.5"
                    stroke="currentColor"
                    className="size-5"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M13.5 4.5 21 12m0 0-7.5 7.5M21 12H3"
                    />
                  </svg>
                )}
              </button>
            </motion.div>
          )}

          {/* Monthly Summary Button - Show when there are logs OR no-fish days in current month */}
          {(datesWithLogs.some(date => {
            const logDate = new Date(date);
            return logDate.getMonth() === currentMonth && logDate.getFullYear() === currentYear;
          }) || datesWithNoFish.some(date => {
            const logDate = new Date(date);
            return logDate.getMonth() === currentMonth && logDate.getFullYear() === currentYear;
          })) && (
            <motion.div
              className="flex items-center justify-center w-full"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
            >
              <button
                onClick={() => {
                  const from = searchParams.get('from');
                  const monthlyStatsUrl = `/log-history/${person.id}/monthly-stats?month=${currentMonth}&year=${currentYear}`;
                  navigate(from ? `${monthlyStatsUrl}&from=${from}` : monthlyStatsUrl);
                }}
                className="flex items-center justify-center button-small button-bg-grad-green gap-3 !px-6 mb-2"
              >
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="size-6">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M7.5 14.25v2.25m3-4.5v4.5m3-6.75v6.75m3-9v9M6 20.25h12A2.25 2.25 0 0 0 20.25 18V6A2.25 2.25 0 0 0 18 3.75H6A2.25 2.25 0 0 0 3.75 6v12A2.25 2.25 0 0 0 6 20.25Z" />
                </svg>
                <span>{t("logHistory_monthlySummary")}</span>
              </button>
            </motion.div>
          )}
        </div>

        <div className="bottom-nav-bar flex justify-center w-full sticky bottom-0 px-scr py-4 bg-white bg-opacity-75 backdrop-blur z-10 border-t">
          <button
            onClick={backInfo.action}
            className="button-small button-bg-grad-2 !text-lg flex flex-row gap-1 items-center"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth="1.5"
              stroke="currentColor"
              className="size-6"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M10.5 19.5 3 12m0 0 7.5-7.5M3 12h18"
              />
            </svg>
            <span>{t(backInfo.text)}</span>
          </button>
        </div>
      </div>
      {modalChildren && (
        <ModalWindow isOpen={!!modalChildren} onClose={modalCloseHandler}>
          {modalChildren}
        </ModalWindow>
      )}
    </motion.div>
  );
}
